import React from 'react';

/**
 * Depth-of-field thumbnails — rendered-style SVGs showing how each
 * aperture choice looks on the shared sphere subject. Same 120×90
 * stage and same sphere geometry as the lighting / shadow cards so a
 * vendor scanning all three reads them as "the same scene, different
 * controls". The SUBJECT stays crisp in every variant — what changes
 * is the BACKGROUND blur + the presence of bokeh highlights.
 *
 * Design rules:
 *   - Sphere centered at (60, 42) r=22, horizon y=62 (shared across sets).
 *   - SVG <feGaussianBlur> on background elements for "shallow" /
 *     "very-shallow" — the subject is rendered outside the filter
 *     group so it stays pin-sharp.
 *   - Bokeh circles are pale cream highlights drawn inside the blur
 *     group; they read as "out-of-focus specular highlights".
 */

const baseStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4/3',
    display: 'block',
};

const SPHERE_CX = 60;
const SPHERE_CY = 42;
const SPHERE_R = 22;
const HORIZON = 62;

const Floor: React.FC<{ fill: string }> = ({ fill }) => (
    <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill={fill} />
);

const Sphere: React.FC<{ gradId: string }> = ({ gradId }) => (
    <>
        <defs>
            <radialGradient id={gradId} cx="42%" cy="30%" r="80%">
                <stop offset="0%" stopColor="#EFEEEB" />
                <stop offset="55%" stopColor="#A8A7A4" />
                <stop offset="100%" stopColor="#4A4945" />
            </radialGradient>
        </defs>
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill={`url(#${gradId})`} />
    </>
);

const Shadow: React.FC<{ rx?: number; opacity?: number }> = ({ rx = SPHERE_R + 8, opacity = 0.3 }) => (
    <ellipse cx={SPHERE_CX} cy={HORIZON + 2} rx={rx} ry={5} fill="#1F2022" opacity={opacity} />
);

// ─── sharp throughout ────────────────────────────────────────────
// Everything crisp — sphere + floor + two distant accent objects
// (a plant sprig upper-right, a small dish lower-left) that stay
// rendered at full fidelity, signaling "deep focus / catalog".
export const SharpDOF: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        {/* distant accent — plant sprig upper-right, crisp */}
        <path d="M 98 26 Q 104 18 114 22" fill="none" stroke="#7B8457" strokeWidth="1.2" opacity="0.8" strokeLinecap="round" />
        <ellipse cx={103} cy={22} rx={3} ry={1.6} fill="#8F9668" opacity="0.8" transform="rotate(-25 103 22)" />
        <ellipse cx={110} cy={22} rx={2.8} ry={1.5} fill="#8F9668" opacity="0.75" transform="rotate(-10 110 22)" />
        {/* distant dish lower-left, crisp */}
        <ellipse cx={14} cy={70} rx={6} ry={2} fill="#A0A19E" opacity="0.6" />
        <Shadow />
        <Sphere gradId="dof-sharp-sphere" />
    </svg>
);

// ─── shallow ─────────────────────────────────────────────────────
// Subject crisp, background elements blurred with medium strength.
// Two soft bokeh highlights hint at out-of-focus specular catches.
export const ShallowDOF: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <filter id="dof-shallow-blur" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="1.3" />
            </filter>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        {/* background elements + bokeh — all blurred */}
        <g filter="url(#dof-shallow-blur)">
            <path d="M 98 26 Q 104 18 114 22" fill="none" stroke="#7B8457" strokeWidth="1.4" opacity="0.75" />
            <ellipse cx={103} cy={22} rx={3.5} ry={2} fill="#8F9668" opacity="0.65" transform="rotate(-25 103 22)" />
            <ellipse cx={110} cy={22} rx={3.2} ry={1.8} fill="#8F9668" opacity="0.6" transform="rotate(-10 110 22)" />
            <ellipse cx={14} cy={70} rx={7} ry={2.5} fill="#A0A19E" opacity="0.55" />
            {/* soft bokeh catches */}
            <circle cx={16} cy={28} r={6} fill="#F0EFEB" opacity="0.55" />
            <circle cx={106} cy={50} r={5} fill="#F0EFEB" opacity="0.5" />
        </g>
        <Shadow />
        <Sphere gradId="dof-shallow-sphere" />
    </svg>
);

// ─── very shallow ────────────────────────────────────────────────
// Strong background blur — prominent bokeh dominates, subject pops.
// Signature macro / f/1.4 look.
export const VeryShallowDOF: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <filter id="dof-vshallow-blur" x="-25%" y="-25%" width="150%" height="150%">
                <feGaussianBlur stdDeviation="2.8" />
            </filter>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        {/* strongly blurred background — the bokeh IS the background */}
        <g filter="url(#dof-vshallow-blur)">
            <circle cx={14} cy={28} r={11} fill="#F0EFEB" opacity="0.8" />
            <circle cx={108} cy={24} r={12} fill="#F0EFEB" opacity="0.78" />
            <circle cx={20} cy={52} r={8} fill="#F0EFEB" opacity="0.65" />
            <circle cx={104} cy={52} r={9} fill="#F0EFEB" opacity="0.7" />
            <circle cx={8} cy={72} r={6} fill="#F0EFEB" opacity="0.55" />
            {/* a faded green splash — hints at blurred foliage behind */}
            <ellipse cx={90} cy={36} rx={14} ry={6} fill="#8F9668" opacity="0.35" />
        </g>
        <Shadow opacity={0.28} />
        <Sphere gradId="dof-vshallow-sphere" />
    </svg>
);

export const DOF_ILLUSTRATIONS: Record<string, React.FC> = {
    'sharp': SharpDOF,
    'shallow': ShallowDOF,
    'very-shallow': VeryShallowDOF,
};
